import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Info } from 'lucide-react';
import { AuthProvider } from '../contexts/AuthContext';
import { OrganizationProvider } from '../contexts/OrganizationContext';
import Layout from '../components/Layout';
import { backendApiClient } from '../services/BackendApiClient';
import { AnalyticsEvents, clearOrganization, track } from '../services/AnalyticsService';
import { DemoBackend, DEMO_NOTICE_EVENT } from './DemoBackend';
import { DemoSessionContext, type DemoLocationState, type DemoSession } from './DemoSession';

function DemoNotice() {
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    const show = (event: Event) => {
      setMessage((event as CustomEvent<string>).detail);
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => setMessage(null), 4000);
    };
    window.addEventListener(DEMO_NOTICE_EVENT, show);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener(DEMO_NOTICE_EVENT, show);
    };
  }, []);

  if (!message) return null;
  return (
    <div role="status" className="fixed z-[60] top-4 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-slate-900 text-white text-sm px-4 py-2.5 rounded-xl shadow-lg animate-slide-up">
      <Info className="w-4 h-4 text-amber-300 flex-shrink-0" />
      {message}
    </div>
  );
}

// A sibling rendered before the providers: React runs sibling effects in order, so demo mode is on before
// their fetches even when StrictMode unmounts and remounts every effect.
function DemoMode({ backend, session }: { backend: DemoBackend; session: DemoSession }) {
  useEffect(() => {
    backendApiClient.enterDemoMode(backend);
    clearOrganization();
    track(AnalyticsEvents.DemoStarted, { loggedIn: session.viewer !== null });
    return () => backendApiClient.exitDemoMode();
  }, [backend, session]);
  return null;
}

export default function DemoRoot() {
  const location = useLocation();
  const [session] = useState<DemoSession>(() => {
    const viewer = (location.state as DemoLocationState | null)?.demoViewer ?? null;
    return { viewer, exitPath: viewer ? '/organizations' : '/' };
  });
  const [backend] = useState(() => new DemoBackend(session.viewer ?? undefined));

  return (
    <DemoSessionContext.Provider value={session}>
      <DemoMode backend={backend} session={session} />
      <AuthProvider>
        <OrganizationProvider>
          <Layout />
          <DemoNotice />
        </OrganizationProvider>
      </AuthProvider>
    </DemoSessionContext.Provider>
  );
}
